import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { AppFlowService } from 'src/app/services/app-flow.service';
import { STORAGE_KEYS } from '../../components/dynamic-inputs/util/constants';
import { TokenDispatcherService } from 'src/app/services/token-dispatcher.service';

@Component({
  selector: 'app-sms-verification-page',
  templateUrl: './sms-verification-page.component.html',
  styleUrls: ['./sms-verification-page.component.scss']
})
export class SmsVerificationPageComponent implements OnInit, OnDestroy {
  otpDigits = Array(6).fill(0);
  formGroup: FormGroup;
  isLoading = false;
  telefone = '';
  isSubmited = false;
  countdownSeconds = 60;
  private countdownTimer: any;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService,
    private appFlowService: AppFlowService,
    private tokenDispatcherService: TokenDispatcherService
  ) {
    this.formGroup = this.fb.group({
      digit0: ['', [Validators.required, Validators.pattern(/[0-9]/)]],
      digit1: ['', [Validators.required, Validators.pattern(/[0-9]/)]],
      digit2: ['', [Validators.required, Validators.pattern(/[0-9]/)]],
      digit3: ['', [Validators.required, Validators.pattern(/[0-9]/)]],
      digit4: ['', [Validators.required, Validators.pattern(/[0-9]/)]],
      digit5: ['', [Validators.required, Validators.pattern(/[0-9]/)]]
    });
  }

  ngOnInit(): void {
    const telefoneCompleto = this.sessionStorageService.getSessionEntry(STORAGE_KEYS.TELEFONE_PESSOA);
    if (telefoneCompleto) {
      this.telefone = telefoneCompleto;
    }
    
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  startCountdown(): void {
    this.countdownSeconds = 60;
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    this.countdownTimer = setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        clearInterval(this.countdownTimer);
        this.countdownSeconds = 0;
      }
    }, 1000);
  }

  getFormControl(index: number): FormControl {
    return this.formGroup.get(`digit${index}`) as FormControl;
  }

  isValid(): boolean {
    return this.formGroup.valid && this.getOtpValue().length === 6;
  }

  getOtpValue(): string {
    return Array(6).fill(0).map((_, i) => this.getFormControl(i).value || '').join('');
  }

  onOtpInput(event: any, index: number): void {
    const value = event.target.value;
    
    if (!/^[0-9]*$/.test(value)) {
      event.target.value = '';
      this.getFormControl(index).setValue('');
      return;
    }

    if (value && index < 5) {
      const nextInput = event.target.parentElement.children[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !target.value && index > 0) {
      const prevInput = target.parentElement?.children[index - 1] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
        prevInput.value = '';
        this.getFormControl(index - 1).setValue('');
      }
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text').slice(0, 6);
    
    if (pastedData && /^[0-9]*$/.test(pastedData)) {
      pastedData.split('').forEach((digit, index) => {
        if (index < 6) {
          this.getFormControl(index).setValue(digit);
        }
      });
      
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      const nextInput = event.target as HTMLElement;
      const parent = nextInput.parentElement;
      if (parent && lastFilledIndex < 5) {
        const nextField = parent.children[lastFilledIndex + 1] as HTMLInputElement;
        if (nextField) {
          nextField.focus();
        }
      }
    }
  }

  async continuar(): Promise<void> {
    if (!this.isValid()) return;

    this.isLoading = true;
    this.isSubmited = true;
    
    try {
      const token = this.getOtpValue();
      
      // ✅ Usar validateToken do TokenDispatcherService
      const stepNameToken = this.appFlowService.getAppState().proposta?.etapaAtual.nome;
      const result = await this.tokenDispatcherService
        .validateToken(this.telefone, token, stepNameToken)
        .toPromise();
      
      
      if (!result?.success) {
        throw new Error(result?.message || 'Código SMS inválido');
      }
      
      const stepName = await this.appFlowService.changeStep({});
      
      if (stepName) {
        await this.router.navigate([stepName]);
      } else {
        throw new Error('Step name is null or undefined');
      }
    } catch (error: any) {
      console.error('Erro na validação SMS:', error);
      
      const errorMessage = error?.message || 'Código SMS inválido ou expirado.';
      
      this.dialog.open(DialogComponent, {
        width: '250px',
        data: { 
          title: 'Erro', 
          message: errorMessage 
        },
      });
      
      this.isLoading = false;
      this.isSubmited = false;
    }
  }

  async reenviar(): Promise<void> {
    if (this.countdownSeconds > 0 || this.isLoading) return;
    
    this.isLoading = true;
    
    try {
      // ✅ Usar sendToken do TokenDispatcherService
      const propostaUUID = this.appFlowService.getAppState().proposta?.id;
      const stepNameToken = this.appFlowService.getAppState().proposta?.etapaAtual.nome;
      const result = await this.tokenDispatcherService
        .sendToken(this.telefone, stepNameToken, propostaUUID)
        .toPromise();
      
      
      if (!result?.success) {
        throw new Error(result?.message || 'Erro ao reenviar código');
      }
      
      this.dialog.open(DialogComponent, {
        width: '250px',
        data: { 
          title: 'Sucesso', 
          message: result?.message || 'Código SMS enviado novamente.' 
        },
      });
      
      Array(6).fill(0).forEach((_, i) => {
        this.getFormControl(i).setValue('');
      });
      
      setTimeout(() => {
        const firstInput = document.querySelector('.otp-input') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
      
      this.startCountdown();
    } catch (error: any) {
      console.error('Erro ao reenviar SMS:', error);
      
      const errorMessage = error?.message || 'Erro ao reenviar código. Tente novamente.';
      
      this.dialog.open(DialogComponent, {
        width: '250px',
        data: { 
          title: 'Erro', 
          message: errorMessage 
        },
      });
    } finally {
      this.isLoading = false;
    }
  }
}